import { nowUtcIso } from '@/lib/dates'
import { newUuid } from '@/offline/queue'
import type { Category } from '@/offline/types'

export interface CategoryUpdateChanges {
  name?: string
  brand_code?: string | null
  sort_order?: number
  archived?: boolean
}

export interface BuiltCategoryUpdate {
  category: Category
  expectedRevision: number
  payload: Record<string, unknown>
}

/**
 * Build a category edit as an update of the SAME uuid (rename keeps device links intact;
 * archive hides from new entry without deleting history). Revision-checked server-side.
 */
export function buildCategoryUpdate(category: Category, changes: CategoryUpdateChanges): BuiltCategoryUpdate {
  const now = nowUtcIso()
  const updated: Category = {
    ...category,
    name: changes.name !== undefined ? changes.name.trim() : category.name,
    brand_code: changes.brand_code !== undefined ? changes.brand_code : category.brand_code,
    sort_order: changes.sort_order !== undefined ? changes.sort_order : category.sort_order,
    archived_at:
      changes.archived === true
        ? (category.archived_at ?? now)
        : changes.archived === false
          ? null
          : category.archived_at,
    revision: category.revision + 1,
    updated_at: now,
  }
  const payload: Record<string, unknown> = {}
  if (changes.name !== undefined) payload.name = updated.name
  if (changes.brand_code !== undefined) payload.brand_code = updated.brand_code
  if (changes.sort_order !== undefined) payload.sort_order = updated.sort_order
  if (changes.archived !== undefined) payload.archived = changes.archived ? 'true' : 'false'
  return { category: updated, expectedRevision: category.revision, payload }
}

export interface BuiltCategoryCreate {
  category: Category
  payload: Record<string, unknown>
}

export function buildCategoryCreate(workspaceId: string, name: string, brandCode: string | null, sortOrder: number): BuiltCategoryCreate {
  const now = nowUtcIso()
  const category: Category = {
    workspace_id: workspaceId,
    id: newUuid(),
    name: name.trim(),
    brand_code: brandCode,
    icon: null,
    color: null,
    sort_order: sortOrder,
    seed_key: null,
    revision: 1,
    created_at: now,
    updated_at: now,
    archived_at: null,
    deleted_at: null,
  }
  return { category, payload: { name: category.name, brand_code: category.brand_code, sort_order: category.sort_order } }
}
